import React, { useEffect, useMemo, useState } from "react";
import {
  deleteAdminSubscriber,
  getAdminSubscribers,
} from "../../../api/subscribeAdmin";
import { useNotification } from "../../../context/NotificationContext";
import { DashboardReloadButton } from "./dashboardUi";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const SubscribersManager = () => {
  const { notify } = useNotification();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await getAdminSubscribers();
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      notify({
        type: "error",
        title: "Failed to load subscribers",
        message: error?.response?.data?.message || error?.message || "",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const filteredItems = useMemo(() => {
    const sorted = [...items].sort(
      (a, b) =>
        new Date(b?.createdAtUtc || 0).getTime() -
        new Date(a?.createdAtUtc || 0).getTime()
    );
    if (filter === "active") return sorted.filter((s) => s?.isActive);
    if (filter === "inactive") return sorted.filter((s) => !s?.isActive);
    return sorted;
  }, [items, filter]);

  const counts = useMemo(() => {
    let active = 0;
    let inactive = 0;
    for (const item of items) {
      if (item?.isActive) active += 1;
      else inactive += 1;
    }
    return { total: items.length, active, inactive };
  }, [items]);

  const handleDelete = async (item) => {
    const ok = window.confirm(
      `Delete subscriber "${item?.email || `#${item?.id}`}"?`
    );
    if (!ok) return;
    try {
      await deleteAdminSubscriber({ id: item.id });
      notify({ type: "success", title: "Subscriber deleted" });
      await loadItems();
    } catch (error) {
      notify({
        type: "error",
        title: "Delete failed",
        message: error?.response?.data?.message || error?.message || "",
      });
    }
  };

  const handleCopyAll = async () => {
    const emails = filteredItems.map((s) => s?.email).filter(Boolean).join(", ");
    if (!emails) {
      notify({ type: "error", title: "No emails to copy" });
      return;
    }
    try {
      await navigator.clipboard.writeText(emails);
      notify({
        type: "success",
        title: "Copied to clipboard",
        message: `${filteredItems.length} email(s)`,
      });
    } catch {
      notify({
        type: "error",
        title: "Copy failed",
        message: "Your browser blocked clipboard access.",
      });
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          onClick={handleCopyAll}
          className="inline-flex items-center rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Copy emails
        </button>
        <DashboardReloadButton onClick={loadItems} loading={loading} />
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
            <p className="text-xs uppercase text-slate-500">Total</p>
            <p className="text-lg font-semibold text-[#0b1f3f]">{counts.total}</p>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
            <p className="text-xs uppercase text-emerald-700">Active</p>
            <p className="text-lg font-semibold text-emerald-800">{counts.active}</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p className="text-xs uppercase text-slate-500">Inactive</p>
            <p className="text-lg font-semibold text-slate-700">{counts.inactive}</p>
          </div>
        </div>

      <div className="flex flex-wrap items-center gap-2">
        {[
          { key: "all", label: "All" },
          { key: "active", label: "Active" },
          { key: "inactive", label: "Unsubscribed" },
        ].map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => setFilter(opt.key)}
            className={`px-3 py-1.5 rounded-lg text-sm border transition ${
              filter === opt.key
                ? "bg-[#0b1f3f] text-white border-[#0b1f3f]"
                : "border-slate-300 text-slate-700 hover:bg-slate-50"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="dashboard-table-wrap dashboard-h-scroll-light overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full table-fixed">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">
                ID
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">
                Email
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">
                Status
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">
                Subscribed
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">
                Unsubscribed
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-sm text-slate-500">
                  Loading subscribers...
                </td>
              </tr>
            ) : filteredItems.length ? (
              filteredItems.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-slate-200 align-top"
                >
                  <td className="px-3 py-3 text-sm text-slate-700">{item.id}</td>
                  <td className="px-3 py-3 text-sm text-slate-900 font-medium break-all">
                    {item.email || "-"}
                  </td>
                  <td className="px-3 py-3 text-sm">
                    {item.isActive ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                        Unsubscribed
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-sm text-slate-700">
                    {formatDate(item.createdAtUtc)}
                  </td>
                  <td className="px-3 py-3 text-sm text-slate-700">
                    {formatDate(item.unsubscribedAtUtc)}
                  </td>
                  <td className="px-3 py-3">
                    <button
                      type="button"
                      onClick={() => handleDelete(item)}
                      className="text-xs px-2 py-1.5 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-sm text-slate-500">
                  No subscribers found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default SubscribersManager;
